import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiOrder, OrderStatus, UserProfile, changeUserPassword, getUserOrders, updateUserProfile, startKyc } from '../api/api';
import { useApp } from '../context/AppContext';
import Button from '../components/Button';
import { clientErrorMessage } from '../utils/clientError';
import { useKycAutoRefresh } from '../hooks/useKycAutoRefresh';
import Card from '../components/Card';
import Input from '../components/Input';
import Table from '../components/Table';

const ORDERS_LIMIT = 20;
const DATE_MIN = '1900-01-01';
const TODAY_ISO = formatInputDate(new Date());
const PHONE_DIGITS_LIMIT = 11;

const KYC_GUIDE_STEPS = [
  {
    title: 'Откройте проверку и выберите русский язык',
    description: 'На первом экране переключите язык на Ru и нажмите «Продолжить».',
    image: '/kyc-guide/01-start.png',
  },
  {
    title: 'Нажмите «Начать проверку»',
    description: 'На экране со списком этапов нажмите кнопку начала проверки. Sumsub ID включать необязательно.',
    image: '/kyc-guide/02-overview.png',
  },
  {
    title: 'Заполните персональные данные',
    description: 'Введите данные кириллицей точно как в паспорте. Не перепутайте имя и фамилию, укажите адрес и телефон владельца паспорта.',
    image: '/kyc-guide/03-personal-data.png',
  },
  {
    title: 'Выберите документ',
    description: 'Страна выдачи — Россия, тип документа — «Внутренний паспорт».',
    image: '/kyc-guide/04-document-type.png',
  },
  {
    title: 'Загрузите паспорт',
    description: 'Загрузите страницу с фотографией. Фото должно быть чётким, без бликов, все данные должны читаться.',
    image: '/kyc-guide/05-document-upload.png',
  },
  {
    title: 'Пройдите проверку лица',
    description: 'Разрешите доступ к камере, снимите головной убор и очки, расположите лицо внутри рамки.',
    image: '/kyc-guide/06-liveness.png',
  },
  {
    title: 'Подтвердите номер телефона',
    description: 'Введите российский номер владельца паспорта, который привязан к приложению банка, и запросите код.',
    image: '/kyc-guide/07-phone.jpg',
  },
  {
    title: 'Введите код из СМС',
    description: 'Введите шестизначный проверочный код. Если СМС не пришло, дождитесь окончания таймера и запросите код повторно.',
    image: '/kyc-guide/08-phone-code.jpg',
  },
] as const;

type ProfileForm = {
  bingxUid: string;
  lastName: string;
  firstName: string;
  middleName: string;
  birthDate: string;
  phone: string;
};

const EMPTY_PROFILE_FORM: ProfileForm = {
  bingxUid: '',
  lastName: '',
  firstName: '',
  middleName: '',
  birthDate: '',
  phone: '',
};

const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  kyc_required: 'Нужен KYC',
  kyc_pending: 'KYC',
  ready_to_pay: 'Готов',
  invoice_created: 'Ожидание оплаты',
  paid: 'Оплачено',
  processing: 'В работе',
  completed: 'Выполнено',
  failed: 'Ошибка',
  expired: 'Истекло',
};

function profileFormFromUser(user: UserProfile): ProfileForm {
  return {
    bingxUid: user.bingxUid || '',
    lastName: user.lastName || '',
    firstName: user.firstName || '',
    middleName: user.middleName || '',
    birthDate: formatProfileDateForInput(user.birthDate || ''),
    phone: formatRussianPhone(user.phone || ''),
  };
}

function normalizeProfileForm(form: ProfileForm): ProfileForm {
  return {
    bingxUid: form.bingxUid.trim(),
    lastName: normalizeRussianName(form.lastName),
    firstName: normalizeRussianName(form.firstName),
    middleName: normalizeRussianName(form.middleName),
    birthDate: normalizeProfileDate(form.birthDate),
    phone: formatRussianPhone(form.phone),
  };
}

function isProfileFormComplete(form: ProfileForm) {
  return Boolean(
    isRussianNamePartValid(form.lastName, true) &&
    isRussianNamePartValid(form.firstName, true) &&
    isRussianNamePartValid(form.middleName, false) &&
    isProfileDateValid(form.birthDate) &&
    isRussianPhoneComplete(form.phone),
  );
}

function normalizeRussianName(value: string) {
  return value.replace(/\s+/g, ' ').trim();
}

function handleRussianNameInput(value: string) {
  return value.replace(/[^А-ЯЁа-яё -]/g, '').replace(/\s+/g, ' ');
}

function isRussianNamePartValid(value: string, required: boolean) {
  const name = normalizeRussianName(value);
  if (!name) return !required;
  if (!/^[А-ЯЁа-яё]+(?:[ -][А-ЯЁа-яё]+)*$/.test(name)) return false;
  return !isAllUppercaseRussianName(name);
}

function isAllUppercaseRussianName(value: string) {
  const letters = value.replace(/[^А-ЯЁа-яё]/g, '');
  return Boolean(letters && /[А-ЯЁ]/.test(letters) && !/[а-яё]/.test(letters));
}

function russianNameError(value: string, required: boolean) {
  const name = normalizeRussianName(value);
  if (!name && !required) return '';
  if (!name && required) return '';
  if (!/^[А-ЯЁа-яё]+(?:[ -][А-ЯЁа-яё]+)*$/.test(name)) {
    return 'Введите кириллицей, как в паспорте.';
  }
  if (isAllUppercaseRussianName(name)) {
    return 'Не вводите ФИО полностью заглавными буквами.';
  }
  return '';
}

function formatInputDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function normalizeProfileDate(value: string) {
  if (!isProfileDateValid(value)) return '';
  const [day, month, year] = value.split('.');
  return `${year}-${month}-${day}`;
}

function isProfileDateValid(value: string) {
  if (!/^\d{2}\.\d{2}\.\d{4}$/.test(value)) return false;

  const [dayRaw, monthRaw, yearRaw] = value.split('.');
  const day = Number(dayRaw);
  const month = Number(monthRaw);
  const year = Number(yearRaw);
  const date = new Date(year, month - 1, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return false;
  }

  const iso = `${yearRaw}-${monthRaw}-${dayRaw}`;
  return iso >= DATE_MIN && iso <= TODAY_ISO;
}

function handleProfileDateInput(value: string) {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
}

function formatProfileDateForInput(value: string | null) {
  if (!value) return '';
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(value)) return value;

  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '';
  return `${match[3]}.${match[2]}.${match[1]}`;
}

function getRussianPhoneDigits(value: string) {
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';

  const normalized = digits[0] === '8'
    ? `7${digits.slice(1)}`
    : digits[0] === '7'
      ? digits
      : `7${digits}`;

  return normalized.slice(0, PHONE_DIGITS_LIMIT);
}

function formatRussianPhone(value: string) {
  const digits = getRussianPhoneDigits(value);
  if (!digits) return '';

  const rest = digits.slice(1);
  let result = '+7';

  if (rest.length > 0) result += ` (${rest.slice(0, 3)}`;
  if (rest.length >= 3) result += ')';
  if (rest.length > 3) result += ` ${rest.slice(3, 6)}`;
  if (rest.length > 6) result += `-${rest.slice(6, 8)}`;
  if (rest.length > 8) result += `-${rest.slice(8, 10)}`;

  return result;
}

function normalizeRussianPhoneForSubmit(value: string) {
  const digits = getRussianPhoneDigits(value);
  return digits.length === PHONE_DIGITS_LIMIT ? `+${digits}` : '';
}

function isRussianPhoneComplete(value: string) {
  return getRussianPhoneDigits(value).length === PHONE_DIGITS_LIMIT;
}

function kycProfileStatusText(status: string, isSbpVerified: boolean) {
  return isSbpVerified || status === 'Approved' ? 'KYC пройден' : 'Ожидает KYC';
}

function formatRub(value: number) {
  return `${value.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽`;
}

function formatUsd(value: number) {
  return `${value.toFixed(6)} USDT`;
}

function formatDateTime(value: string | null) {
  return value ? new Date(value).toLocaleString('ru-RU') : '-';
}

function formatDate(value: string | null) {
  if (!value) return '-';
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(value)) return value;
  return new Date(value).toLocaleDateString('ru-RU');
}

function orderStatusClass(status: OrderStatus) {
  if (['completed', 'paid'].includes(status)) return 'status-completed';
  if (['failed', 'expired'].includes(status)) return 'status-failed';
  if (['processing'].includes(status)) return 'status-processing';
  return 'status-pending';
}

export default function ProfilePage() {
  const { userAuth } = useApp();
  useKycAutoRefresh();
  const user = userAuth.user;

  const [form, setForm] = useState<ProfileForm>(() => {
    if (!user) return EMPTY_PROFILE_FORM;
    return profileFormFromUser(user);
  });
  const [profileError, setProfileError] = useState('');
  const [profileSaved, setProfileSaved] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSaved, setPasswordSaved] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  const [kycError, setKycError] = useState('');
  const [kycNotice, setKycNotice] = useState('');
  const [kycLoading, setKycLoading] = useState(false);
  const [kycModalOpen, setKycModalOpen] = useState(false);
  const [kycConfirmed, setKycConfirmed] = useState(false);

  const [orders, setOrders] = useState<ApiOrder[]>([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState('');
  const [ordersRefreshKey, setOrdersRefreshKey] = useState(0);

  useEffect(() => {
    if (!user) {
      setForm(EMPTY_PROFILE_FORM);
      return;
    }

    setForm(profileFormFromUser(user));
  }, [user]);

  useEffect(() => {
    if (!userAuth.token) {
      setOrders([]);
      setOrdersTotal(0);
      setOrdersPage(1);
      setOrdersRefreshKey(0);
      return;
    }

    let cancelled = false;
    setOrdersLoading(true);
    setOrdersError('');

    getUserOrders(userAuth.token, { page: ordersPage, limit: ORDERS_LIMIT })
      .then(res => {
        if (cancelled) return;
        setOrders(res.items);
        setOrdersTotal(res.total);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setOrders([]);
        setOrdersTotal(0);
        setOrdersError(clientErrorMessage(err, 'Не удалось загрузить историю пополнений.'));
      })
      .finally(() => {
        if (!cancelled) setOrdersLoading(false);
      });

    return () => { cancelled = true; };
  }, [ordersPage, ordersRefreshKey, userAuth.token]);

  const profileComplete = useMemo(() => isProfileFormComplete(form), [form]);

  if (!userAuth.token || !user) {
    return (
      <div className="profile-guest-layout">
        <div className="profile-guest-copy">
          <div className="brand"><span className="brand-dot"></span><span className="brand-name">Профиль</span></div>
          <h1 className="landing-headline">Войдите, чтобы управлять аккаунтом</h1>
          <p className="landing-sub">
            В профиле хранятся анкета, статус KYC и данные для создания платежей.
          </p>
        </div>
        <Card className="profile-guest-card">
          <h2 className="success-title" style={{ textAlign: 'left' }}>Доступ к профилю</h2>
          <p className="success-sub" style={{ textAlign: 'left' }}>
            Авторизуйтесь в аккаунте или создайте новый, если еще не регистрировались.
          </p>
          <div className="profile-guest-actions">
            <Link to="/login"><Button fullWidth size="lg">Войти</Button></Link>
            <Link to="/register"><Button fullWidth variant="secondary">Создать аккаунт</Button></Link>
          </div>
          <div className="profile-guest-list">
            <span>Анкета и KYC</span>
            <span>Анкетные данные для платежей</span>
            <span>История и статусы заявок</span>
          </div>
        </Card>
      </div>
    );
  }

  const currentUser = user;
  const questionnaireLocked = profileSaved || isProfileFormComplete(profileFormFromUser(user));
  const isKycApproved = user.kycStatus === 'Approved';
  const canShowKyc = questionnaireLocked;
  const canStartKyc = canShowKyc && !isKycApproved;
  const ordersPages = Math.max(1, Math.ceil(ordersTotal / ORDERS_LIMIT));
  const nameErrors = {
    lastName: russianNameError(form.lastName, true),
    firstName: russianNameError(form.firstName, true),
    middleName: russianNameError(form.middleName, false),
  };

  function updateField<K extends keyof ProfileForm>(key: K, value: ProfileForm[K]) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!userAuth.token || questionnaireLocked) return;
    setProfileLoading(true);
    setProfileError('');
    setProfileSaved(false);
    const submitted = normalizeProfileForm(form);
    const payloadForm = {
      ...submitted,
      phone: normalizeRussianPhoneForSubmit(submitted.phone),
    };
    const { bingxUid, ...profileWithoutUid } = payloadForm;
    try {
      const updated = await updateUserProfile(userAuth.token, profileWithoutUid);
      setForm(profileFormFromUser(updated));
      userAuth.setUser(updated);
      setProfileSaved(true);
    } catch (err: any) {
      setProfileError(clientErrorMessage(err, 'Не удалось сохранить профиль. Повторите попытку позже.'));
    } finally {
      setProfileLoading(false);
    }
  }

  async function startKycSession() {
    if (!userAuth.token) return;
    if (!profileComplete) {
      setKycError('Заполните анкету и сохраните профиль перед запуском KYC.');
      setKycNotice('');
      setKycModalOpen(false);
      return;
    }

    setKycLoading(true);
    setKycError('');
    setKycNotice('');
    setKycModalOpen(false);
    try {
      const result = await startKyc(userAuth.token);
      if (!result.kycUrl) {
        throw new Error('Сервис проверки личности не вернул ссылку');
      }

      userAuth.setUser({
        ...currentUser,
        kycStatus: result.status,
        kycUpdatedAt: new Date().toISOString(),
      });

      const popup = window.open(result.kycUrl, '_blank', 'noopener,noreferrer');
      if (!popup) {
        window.location.href = result.kycUrl;
      }
      setKycNotice('KYC открыт в новой вкладке. Если вы закрыли окно, нажмите кнопку еще раз.');
    } catch (err: any) {
      setKycError(clientErrorMessage(err, 'Не удалось открыть проверку личности. Повторите попытку позже.'));
    } finally {
      setKycLoading(false);
    }
  }

  async function changePassword(e: React.FormEvent) {
    e.preventDefault();
    if (!userAuth.token) return;

    setPasswordError('');
    setPasswordSaved(false);
    if (newPassword.length < 8) {
      setPasswordError('Новый пароль должен содержать не менее 8 символов.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Новый пароль и подтверждение не совпадают.');
      return;
    }

    setPasswordLoading(true);
    try {
      await changeUserPassword(userAuth.token, currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordSaved(true);
    } catch (err: any) {
      setPasswordError(clientErrorMessage(err, 'Не удалось изменить пароль. Повторите попытку позже.'));
    } finally {
      setPasswordLoading(false);
    }
  }

  function openKycConfirmation() {
    setKycError('');
    setKycNotice('');
    setKycConfirmed(false);
    setKycModalOpen(true);
  }

  const orderColumns = [
    { key: 'createdAt', header: 'Дата', render: (o: ApiOrder) => <span className="mono text-sm">{formatDateTime(o.createdAt)}</span> },
    { key: 'rub', header: 'RUB', align: 'right' as const, render: (o: ApiOrder) => <span className="mono">{formatRub(o.rub)}</span> },
    { key: 'usd', header: 'USDT', align: 'right' as const, render: (o: ApiOrder) => <span className="mono accent">{formatUsd(o.usd)}</span> },
    {
      key: 'status',
      header: 'Статус',
      render: (o: ApiOrder) => (
        <span className={`status-badge ${orderStatusClass(o.status)}`}>
          {ORDER_STATUS_LABELS[o.status] ?? o.status}
        </span>
      ),
    },
    { key: 'paidAt', header: 'Оплачено', render: (o: ApiOrder) => <span className="mono text-sm">{formatDateTime(o.paidAt)}</span> },
    { key: 'fulfilledAt', header: 'Завершено', render: (o: ApiOrder) => <span className="mono text-sm">{formatDateTime(o.fulfilledAt)}</span> },
  ];

  return (
    <div className="admin-layout" style={{ maxWidth: 960, margin: '24px auto' }}>
      <Card>
        <h2 style={{ marginBottom: 20 }}>Профиль</h2>

        <div className="success-details" style={{ marginBottom: 20 }}>
          <div className="success-row"><span>Email</span><span className="mono">{user.email}</span></div>
          <div className="success-row"><span>Промокод</span><span className="mono">{user.promoCode}</span></div>
          <div className="success-row"><span>Payment client</span><span className="mono">{user.paymentClientId}</span></div>
        </div>

        <h3 style={{ margin: '0 0 14px' }}>Анкета для KYC и платежей</h3>
        {questionnaireLocked ? (
          <>
            <div className="success-details">
              <div className="success-row"><span>ФИО</span><span>{[form.lastName, form.firstName, form.middleName].filter(Boolean).join(' ')}</span></div>
              <div className="success-row"><span>Дата рождения</span><span className="mono">{formatDate(form.birthDate)}</span></div>
              <div className="success-row"><span>Телефон</span><span className="mono">{form.phone}</span></div>
            </div>
            <div className="text-muted text-sm" style={{ marginTop: 12 }}>
              Анкета заполнена и заблокирована. Для изменения обратитесь в поддержку.
            </div>
            {!isKycApproved && (
              <div style={{ color: 'var(--success)', margin: '12px 0 0' }}>
                Профиль заполнен. Пройдите KYC для активации пополнения по СБП.
              </div>
            )}
          </>
        ) : (
          <form onSubmit={save}>
            <div className="profile-form-grid">
              <Input
                label="Фамилия"
                value={form.lastName}
                onChange={e => updateField('lastName', handleRussianNameInput(e.target.value))}
                error={nameErrors.lastName}
                required
              />
              <Input
                label="Имя"
                value={form.firstName}
                onChange={e => updateField('firstName', handleRussianNameInput(e.target.value))}
                error={nameErrors.firstName}
                required
              />
              <Input
                label="Отчество"
                value={form.middleName}
                onChange={e => updateField('middleName', handleRussianNameInput(e.target.value))}
                error={nameErrors.middleName}
              />
              <Input
                label="Телефон РФ"
                type="tel"
                inputMode="tel"
                value={form.phone}
                onChange={e => updateField('phone', formatRussianPhone(e.target.value))}
                placeholder="+7 (999) 999-99-99"
                maxLength={18}
                hint="Укажите номер телефона, который подключен к СБП."
                required
              />
              <Input
                label="Дата рождения"
                type="text"
                inputMode="numeric"
                value={form.birthDate}
                onChange={e => updateField('birthDate', handleProfileDateInput(e.target.value))}
                placeholder="ДД.ММ.ГГГГ"
                maxLength={10}
                required
              />
            </div>

            {profileSaved && (
              <div style={{ color: 'var(--success)', margin: '12px 0 0' }}>
                Профиль заполнен. Пройдите KYC для активации пополнения по СБП.
              </div>
            )}
            {profileError && <div style={{ color: 'var(--danger)', margin: '12px 0 0' }}>{profileError}</div>}
            <Button type="submit" loading={profileLoading} disabled={!profileComplete} style={{ marginTop: 16 }}>
              Сохранить профиль
            </Button>
          </form>
        )}
      </Card>

      <Card>
        <h2 style={{ marginBottom: 20 }}>Безопасность</h2>
        <form onSubmit={changePassword}>
          <div className="profile-form-grid">
            <Input label="Текущий пароль" type="password" autoComplete="current-password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} required />
            <Input label="Новый пароль" type="password" autoComplete="new-password" value={newPassword} onChange={e => setNewPassword(e.target.value)} minLength={8} required />
            <Input label="Повторите новый пароль" type="password" autoComplete="new-password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} minLength={8} required />
          </div>
          {passwordError && <div style={{ color: 'var(--danger)', margin: '12px 0 0' }}>{passwordError}</div>}
          {passwordSaved && <div style={{ color: 'var(--success)', margin: '12px 0 0' }}>Пароль успешно изменён.</div>}
          <Button type="submit" loading={passwordLoading} style={{ marginTop: 16 }}>
            Изменить пароль
          </Button>
        </form>
      </Card>

      {canShowKyc && (
      <div style={{ marginTop: 24 }}>
        <Card>
          <h3 style={{ marginBottom: 8 }}>KYC</h3>
          <p className="text-muted text-sm" style={{ marginBottom: 18 }}>
            KYC проходит на стороне Bitbanker/Sumsub. Для активации пополнения по СБП используйте телефон, который подключен к СБП.
          </p>
          <div className="success-details" style={{ marginBottom: 20 }}>
            <div className="success-row"><span>Статус</span><span className="mono">{kycProfileStatusText(user.kycStatus, user.isSbpVerified)}</span></div>
            {user.kycUpdatedAt && (
              <div className="success-row">
                <span>Обновлено</span>
                <span className="mono">{new Date(user.kycUpdatedAt).toLocaleString('ru-RU')}</span>
              </div>
            )}
          </div>

          {canStartKyc && (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Button type="button" onClick={openKycConfirmation} loading={kycLoading} loadingText={null}>
                Пройти KYC
              </Button>
            </div>
          )}
          {isKycApproved && (
            <Link to="/">
              <Button type="button">Купить USDT</Button>
            </Link>
          )}

          {!profileComplete && (
            <div className="text-muted text-sm" style={{ marginTop: 12 }}>
              Заполните и сохраните анкету, чтобы получить ссылку KYC.
            </div>
          )}
          {kycNotice && <div style={{ color: 'var(--success)', margin: '12px 0 0' }}>{kycNotice}</div>}
          {kycError && <div style={{ color: 'var(--danger)', margin: '12px 0 0' }}>{kycError}</div>}
        </Card>
      </div>
      )}

      <div style={{ marginTop: 24 }}>
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <div>
              <h3 style={{ marginBottom: 6 }}>История пополнений</h3>
              <p className="text-muted text-sm" style={{ marginBottom: 0 }}>
                Все созданные заявки и текущие статусы платежей.
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setOrdersPage(1);
                setOrdersRefreshKey(key => key + 1);
              }}
              loading={ordersLoading}
            >
              Обновить
            </Button>
          </div>

          {ordersError && (
            <div style={{ color: 'var(--danger)', marginBottom: 12 }}>
              {ordersError}
            </div>
          )}

          <Table
            columns={orderColumns}
            data={orders}
            keyExtractor={o => o.id}
            emptyMessage={ordersLoading ? 'Загрузка...' : 'Пополнений пока нет'}
          />

          {ordersPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, marginTop: 14 }}>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={ordersPage <= 1 || ordersLoading}
                onClick={() => setOrdersPage(page => Math.max(1, page - 1))}
              >
                Назад
              </Button>
              <span className="text-muted text-sm">
                {ordersPage} / {ordersPages}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={ordersPage >= ordersPages || ordersLoading}
                onClick={() => setOrdersPage(page => Math.min(ordersPages, page + 1))}
              >
                Вперед
              </Button>
            </div>
          )}
        </Card>
      </div>

      {kycModalOpen && (
        <div className="modal-backdrop" role="presentation" onClick={() => setKycModalOpen(false)}>
          <div
            className="modal-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="kyc-confirmation-title"
            onClick={e => e.stopPropagation()}
          >
            <h3 id="kyc-confirmation-title" style={{ marginBottom: 6 }}>Подготовьтесь к KYC</h3>
            <p className="kyc-modal-lead">
              Это однократная проверка личности для пополнений по СБП. Понадобятся паспорт и селфи, обычно проверка занимает не более минуты.
            </p>
            <div className="kyc-modal-text">
              <div className="kyc-requirement kyc-requirement--critical">
                <span className="kyc-requirement-badge">Важно</span>
                <p><strong>Только паспорт РФ.</strong> Все паспортные данные вводите кириллицей и точно как в документе.</p>
              </div>
              <div className="kyc-requirement">
                <span className="kyc-requirement-number">1</span>
                <p><strong>Не перепутайте имя и фамилию.</strong> В каждом поле укажите соответствующую часть ФИО из паспорта.</p>
              </div>
              <div className="kyc-requirement">
                <span className="kyc-requirement-number">2</span>
                <p><strong>Телефон в формате +7XXXXXXXXXX</strong> должен принадлежать владельцу паспорта и быть привязан к приложению банка.</p>
              </div>
              <div className="kyc-requirement kyc-requirement--technical">
                <span className="kyc-requirement-badge">До старта</span>
                <p><strong>Включите VPN.</strong> Откройте проверку в новой вкладке и пройдите её на одном устройстве, не закрывая окно.</p>
              </div>
              <p className="kyc-modal-note">
                Если паспорт выдан менее месяца назад, данные могут ещё не появиться в базе — в этом случае повторите попытку позже.
              </p>
            </div>
            <div className="kyc-guide" aria-labelledby="kyc-guide-title">
              <div className="kyc-guide-heading">
                <h4 id="kyc-guide-title">Как пройти проверку</h4>
                <span>8 этапов</span>
              </div>
              <p className="kyc-guide-intro">
                Откройте этап, чтобы свериться со скриншотом интерфейса Sumsub.
              </p>
              <div className="kyc-guide-steps">
                {KYC_GUIDE_STEPS.map((step, index) => (
                  <details className="kyc-guide-step" key={step.image} open={index === 0}>
                    <summary>
                      <span className="kyc-guide-step-number">{index + 1}</span>
                      <span>{step.title}</span>
                    </summary>
                    <div className="kyc-guide-step-content">
                      <p>{step.description}</p>
                      <img
                        src={step.image}
                        alt={`Этап ${index + 1}: ${step.title}`}
                        loading="lazy"
                      />
                    </div>
                  </details>
                ))}
              </div>
            </div>
            <label className="modal-check">
              <input
                type="checkbox"
                checked={kycConfirmed}
                onChange={e => setKycConfirmed(e.target.checked)}
              />
              <span>Я понимаю требования и готов продолжить</span>
            </label>
            <div className="modal-actions">
              <Button type="button" variant="ghost" onClick={() => setKycModalOpen(false)}>
                Отмена
              </Button>
              <Button type="button" onClick={startKycSession} loading={kycLoading} loadingText={null} disabled={!kycConfirmed}>
                Создать ссылку KYC
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
